// Scheduled reports (Sprint 8). A report schedule delivers a periodic digest
// (uptime / playback / top media) to Slack for a scope. CRUD over
// /v1/report-schedules. Types live here (not @seencast/shared yet), same pattern
// as api/org.ts and api/analytics.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type { ListEnvelope } from './client.ts';

export type ReportScopeType = 'org' | 'group' | 'screen';
export type ReportFreq = 'daily' | 'weekly';
export type ReportMetric = 'uptime' | 'playback' | 'top_media' | 'attention';

export const REPORT_METRICS: readonly ReportMetric[] = [
  'uptime',
  'playback',
  'top_media',
  'attention',
];

/** Server representation of a stored schedule. `scope_name` is a display
 *  convenience the API resolves; null for org-wide or a since-deleted target. */
export interface ReportSchedule {
  id: string;
  name: string;
  scope_type: ReportScopeType;
  scope_id: string | null;
  scope_name: string | null;
  freq: ReportFreq;
  /** 0=Mon … 6=Sun. null for daily. */
  day_of_week: number | null;
  hour: number; // 0–23
  timezone: string;
  metrics: ReportMetric[];
  /** null = "DM me"; a string = a Slack channel id. */
  slack_target: string | null;
  enabled: boolean;
  created_at: number;
  last_run_at: number | null;
}

/** POST body (day_of_week required only for weekly). */
export interface CreateReportSchedule {
  name: string;
  scope_type: ReportScopeType;
  scope_id?: string;
  freq: ReportFreq;
  day_of_week?: number;
  hour: number;
  timezone: string;
  metrics: ReportMetric[];
  slack_target?: string | null;
}

export type UpdateReportSchedule = Partial<CreateReportSchedule> & { enabled?: boolean };

export function listReportSchedules(): Promise<ReportSchedule[]> {
  return request<ListEnvelope<ReportSchedule>>('/report-schedules').then((r) => r.items);
}

export function useReportSchedules() {
  return useQuery({ queryKey: qk.reportSchedules, queryFn: listReportSchedules });
}

export function useCreateReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReportSchedule) =>
      request<ReportSchedule>('/report-schedules', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.reportSchedules }),
  });
}

export function useUpdateReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateReportSchedule }) =>
      request<ReportSchedule>(`/report-schedules/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.reportSchedules }),
  });
}

export function useDeleteReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/report-schedules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.reportSchedules }),
  });
}
